// Utilità per i layer delle zone PRG su Leaflet: motivi SVG, evidenziazione,
// punto-in-poligono e conversione degli stili.

const SVG_NS = "http://www.w3.org/2000/svg";

export const GLOW_COLOR = "#84e34c"; // --c-green-bright: il "bersaglio" del design system
const GLOW_FILTER = `drop-shadow(0 0 6px ${GLOW_COLOR}) drop-shadow(0 0 12px ${GLOW_COLOR})`;

// ─── Motivi di riempimento (righe, puntini, griglia, …) ─────────────────────

// target: la mappa (renderer SVG di default) o un L.svg() già aggiunto alla mappa.
function svgDefs(target) {
  const root = (target.getRenderer ? target.getRenderer(target) : target)?._container;
  if (!root) return null;
  let defs = root.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(SVG_NS, "defs");
    root.insertBefore(defs, root.firstChild);
  }
  return defs;
}

function el(name, attrs) {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, String(v)));
  return node;
}

// type: stripes | dots | dotted | solid | grid | lines (righe con passo/spessore) | cross
// Ritorna l'id del <pattern> da usare come fill="url(#id)", o null per "dashed"
// (che si rende con dashArray sul bordo).
export function ensurePattern(target, { type, color, angle = 0, step = 8, width = 2, opacity = 1 }) {
  if (!type || type === "dashed") return null;
  const id = `sm-${type}-${color}-${angle}-${step}-${width}-${opacity}`.replace(/[^\w-]/g, "");
  if (document.getElementById(id)) return id;
  const defs = svgDefs(target);
  if (!defs) return null;

  const s = step;
  const pattern = el("pattern", { id, patternUnits: "userSpaceOnUse", width: s, height: s });
  if (angle) pattern.setAttribute("patternTransform", `rotate(${angle} ${s / 2} ${s / 2})`);
  const paint = { fill: color, "fill-opacity": opacity };
  const ink = { stroke: color, "stroke-opacity": opacity, "stroke-width": width };

  if (type === "stripes") pattern.appendChild(el("rect", { width, height: s, ...paint }));
  else if (type === "dots") pattern.appendChild(el("circle", { cx: s / 2, cy: s / 2, r: 2, ...paint }));
  else if (type === "dotted") pattern.appendChild(el("line", { x1: 0, y1: s / 2, x2: s, y2: s / 2, ...ink, "stroke-width": 1, "stroke-dasharray": "2,2" }));
  else if (type === "solid") pattern.appendChild(el("rect", { width: s, height: s, ...paint }));
  else if (type === "grid" || type === "cross") {
    pattern.appendChild(el("line", { x1: 0, y1: 0, x2: 0, y2: s, ...ink }));
    pattern.appendChild(el("line", { x1: 0, y1: 0, x2: s, y2: 0, ...ink }));
  } else if (type === "lines") pattern.appendChild(el("line", { x1: 0, y1: 0, x2: 0, y2: s, ...ink }));
  else return null;

  defs.appendChild(pattern);
  return id;
}

// Applica il motivo al path SVG di un layer appena aggiunto alla mappa.
export function bindPattern(layer, patternId) {
  if (!patternId) return;
  layer._patternId = patternId;
  const apply = () => {
    if (layer._path) layer._path.setAttribute("fill", `url(#${patternId})`);
  };
  layer.on("add", apply);
  apply();
}

// ─── Evidenziazione della zona selezionata ──────────────────────────────────

export function createHighlighter() {
  let current = null;
  const clear = () => {
    const prev = current;
    current = null;
    if (!prev) return;
    if (prev._baseStyle) prev.setStyle(prev._baseStyle);
    if (prev._path) {
      prev._path.style.filter = "";
      if (prev._patternId) prev._path.setAttribute("fill", `url(#${prev._patternId})`);
    }
  };
  const apply = (layer) => {
    if (current && current !== layer) clear();
    layer.setStyle({ color: GLOW_COLOR, weight: 3, fillOpacity: 0.3 });
    if (layer._path) layer._path.style.filter = GLOW_FILTER;
    current = layer;
  };
  return { apply, clear, get current() { return current; } };
}

// ─── Punto in poligono (ray casting, buchi ignorati) ────────────────────────

function pointInRing(lat, lng, ring) {
  if (!Array.isArray(ring)) return false; // alcuni geojson hanno parti vuote
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function pointInGeometry(latlng, geom) {
  if (!geom || !Array.isArray(geom.coordinates)) return false;
  if (geom.type === "Polygon") return pointInRing(latlng.lat, latlng.lng, geom.coordinates[0]);
  if (geom.type === "MultiPolygon") return geom.coordinates.some((p) => Array.isArray(p) && pointInRing(latlng.lat, latlng.lng, p[0]));
  return false;
}

// Layer di zona visibili che contengono il punto (per scegliere tra zone sovrapposte).
export function zonesAt(layers, latlng) {
  return layers.filter((l) => l._map && pointInGeometry(latlng, l.feature?.geometry));
}

// ─── Catasto ────────────────────────────────────────────────────────────────

// "0010B0" → "10B", "000100" → "1"
export function formatFoglio(raw) {
  if (!raw || raw.length < 4) return raw || "—";
  const num = raw.slice(0, 4).replace(/^0+/, "") || "0";
  const letter = raw[4];
  const digit = raw[5];
  if (letter && /[A-Za-z]/.test(letter)) return digit && digit !== "0" ? num + letter + digit : num + letter;
  return num;
}

// Anello esterno della parte più grande: /zoning/zones-in-aoi accetta un solo anello.
export function outerRing(geometry) {
  if (!geometry) return null;
  if (geometry.type === "Polygon") return geometry.coordinates[0];
  if (geometry.type === "MultiPolygon") {
    let best = null;
    let bestLen = -1;
    geometry.coordinates.forEach((poly) => {
      if (poly[0].length > bestLen) { best = poly[0]; bestLen = poly[0].length; }
    });
    return best;
  }
  return null;
}

// ─── Stili dei comuni legacy (styles.json di Osimo, campo style dei gpkg PA) ─

const FILL_PATTERNS = new Set(["stripes", "dots", "dotted", "solid", "grid"]);

// { color, weight, fillColor, fillOpacity, pattern: stripes|dots|…|dashed|empty, angle }
// "empty" (fasce di rispetto, fogli, PPCS, …) e qualsiasi motivo sconosciuto
// sono solo contorno, come nella versione precedente.
export function legacyZoneStyle(style = {}) {
  const fillColor = style.fillColor || style.color || "#ccc";
  const dashed = style.pattern === "dashed";
  const patterned = FILL_PATTERNS.has(style.pattern);
  const outlineOnly = dashed || (!!style.pattern && !patterned);
  return {
    style: {
      color: style.color || "#000",
      weight: style.weight || (outlineOnly ? 1.5 : 1),
      fillColor,
      fillOpacity: outlineOnly ? 0 : (style.fillOpacity ?? 0.6),
      ...(dashed && { dashArray: "8 5" }),
    },
    pattern: patterned ? { type: style.pattern, color: fillColor, angle: style.angle || 0 } : null,
    swatch: {
      background: outlineOnly ? "transparent"
        : patterned && style.pattern !== "solid"
          ? `repeating-linear-gradient(${90 - (style.angle || 0)}deg, ${fillColor} 0 2px, transparent 2px 5px)`
          : fillColor,
      borderColor: style.color || fillColor,
      borderStyle: dashed ? "dashed" : "solid",
    },
  };
}

// ─── Stili delle zone dei gpkg OmniSurv (services/gis_stili.py) ────────────

// Stile di ripiego di gis_stili quando il gpkg non ha stili QGIS: tutto giallo.
const FALLBACK_FILL = "#f2c14e";

// Vincoli e fasce che si sovrappongono alle zone: riempimento leggero e bordo
// tratteggiato, altrimenti coprono le ZTO sottostanti.
const OVERLAY_RE = /^(FASCI[AE]|RISCHIO|R\d+-P\d+|LIMITE|VINCOL|AMBIT[OI][ _]DI[ _]TUTELA|PERIMETR|AREA[ _]URBANA|RISPETTO)/i;

export function isOverlayName(name) {
  return OVERLAY_RE.test(name);
}

function fnv(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

// Tonalità per zona territoriale omogenea, come nei PRG cartacei: A centro
// storico, B completamento, C espansione, D produttive, E agricole, F servizi,
// V verde.
const ZTO_HUE = { A: 8, B: 28, C: 50, D: 282, E: 78, F: 205, G: 180, V: 135 };

// Colore stabile ricavato dal nome della zona, per i gpkg senza stili: le ZTO
// prendono la tonalità della loro lettera (varianti più chiare/scure per le
// sottozone), il resto una tonalità sparsa dall'hash del nome.
export function colorFromName(name) {
  const m = name.match(/^(?:ZONA|ZTO)\s+([A-Z])(.*)$/i);
  const base = m ? ZTO_HUE[m[1].toUpperCase()] : undefined;
  if (base !== undefined) {
    const v = fnv(m[2] || "") % 5;
    return `hsl(${(base + (v - 2) * 5 + 360) % 360}, 72%, ${40 + v * 6}%)`;
  }
  return `hsl(${Math.round((fnv(name) * 137.508) % 360)}, 65%, 52%)`;
}

export function isFallbackStile(stile) {
  return !stile || (stile.fill === FALLBACK_FILL && stile.stroke === FALLBACK_FILL && !stile.motivo);
}

// stile di gis_stili → { style Leaflet, pattern (opzionale), swatch per la legenda }
// opacity: opacità globale di riempimento scelta in legenda (0–1).
export function leafletStyleFromStile(stile, name, opacity) {
  const overlay = isOverlayName(name);
  if (isFallbackStile(stile)) {
    const c = colorFromName(name);
    return {
      style: overlay
        ? { color: c, weight: 2, opacity: 1, dashArray: "6 4", fillColor: c, fillOpacity: opacity * 0.25 }
        : { color: c, weight: 1.2, opacity: 0.9, fillColor: c, fillOpacity: opacity },
      pattern: null,
      swatch: overlay ? { background: "transparent", borderColor: c, borderStyle: "dashed" } : { background: c, borderColor: c },
    };
  }
  const fill = stile.fill || null;
  const motivo = stile.motivo || null;
  const stroke = stile.stroke || fill || motivo?.colore || "#9aa0ad";
  const style = {
    color: stroke,
    opacity: stile.stroke ? Math.max(stile.stroke_opacity ?? 1, 0.35) : 0.6,
    weight: Math.min(Math.max(stile.stroke_width || 1, 0.6), 4),
    dashArray: stile.dash || null,
    fillColor: fill || motivo?.colore || stroke,
    fillOpacity: fill ? (stile.fill_opacity ?? 1) * opacity * (overlay ? 0.35 : 1) : motivo ? 1 : 0,
  };
  const pattern = !fill && motivo
    ? {
        type: motivo.incrociato ? "cross" : "lines",
        color: motivo.colore,
        angle: motivo.angolo || 0,
        step: Math.round(motivo.passo || 8),
        width: motivo.spessore || 1,
        opacity: Math.min(1, (motivo.opacita ?? 1) * Math.max(opacity * 1.6, 0.4)),
      }
    : null;
  return {
    style,
    pattern,
    swatch: {
      background: fill || (motivo ? `repeating-linear-gradient(${90 - (motivo.angolo || 0)}deg, ${motivo.colore} 0 1px, transparent 1px 4px)` : "transparent"),
      borderColor: stroke,
    },
  };
}
